/**
 * PropRuntimeGraph 工具集合。
 *
 * 该文件面向游戏端运行时数据，只依赖 `PropRuntimeGraph` 和轻量描述核心。
 * 运行时图没有编辑器的 `flows / bindings`，连接信息直接写在节点自身的 `next / inputs` 中。
 * 因此这里的主要职责是把运行时连接转换为描述核心可读取的规范化关系。
 */

import type {
  PropRuntimeGraph,
  PropRuntimeInputMap,
  PropRuntimeNextMap,
} from "./PropRuntimeGraph";
import {
  getPropDescription,
  getPropNegativeDescriptionFragments,
  PropDescriptionBinding,
  PropDescriptionFlow,
  PropDescriptionGraph,
  PropDescriptionOptions,
} from "./PropDescriptionCore";

export type PropRuntimeGraphDescriptionOptions = PropDescriptionOptions;

/**
 * 根据运行时道具图生成中文描述。
 *
 * 该函数用于游戏端展示真实运行时数据对应的效果文本。它不会读取编辑器坐标、节点标题或画布注释，
 * 也不会依赖编辑器版 `PropGraph`。
 */
export function getPropRuntimeGraphDescription(
  graph: PropRuntimeGraph,
  options: PropRuntimeGraphDescriptionOptions = {},
) {
  return getPropDescription(createRuntimeDescriptionGraph(graph), { ...options, propType: options.propType ?? graph.type });
}

/**
 * 根据运行时道具图生成负面效果片段。
 *
 * 负面片段用于客户端高亮描述文本。判断标准与编辑器一致：只看动作节点 `data.negative === true`。
 */
export function getPropRuntimeGraphNegativeDescriptionFragments(
  graph: PropRuntimeGraph,
  options: PropRuntimeGraphDescriptionOptions = {},
) {
  return getPropNegativeDescriptionFragments(createRuntimeDescriptionGraph(graph), { ...options, propType: options.propType ?? graph.type });
}

/**
 * 将运行时图转换为描述核心使用的规范化图。
 *
 * 转换规则：
 * - 运行时数字节点 id 转为字符串 id，便于和描述核心统一处理。
 * - `node.next[portId] = [targetId]` 转为流程边。
 * - `node.inputs[portId] = sourceId | sourceId[]` 转为输入绑定。
 * - `sourceId` 仍保留在节点参数外部，不参与描述文本，只用于调试时定位源编辑器节点。
 */
export function createRuntimeDescriptionGraph(graph: PropRuntimeGraph): PropDescriptionGraph {
  const nodes: PropDescriptionGraph["nodes"] = {};
  const flows: PropDescriptionFlow[] = [];
  const bindings: PropDescriptionBinding[] = [];

  graph.graph.nodes.forEach((node) => {
    const nodeId = String(node.id);
    nodes[nodeId] = {
      id: nodeId,
      type: node.type,
      data: node.data,
    };
    appendRuntimeNextFlows(nodeId, node.next, flows);
    appendRuntimeInputBindings(nodeId, node.inputs, bindings);
  });

  return {
    entryNodeIds: graph.graph.entries.map((id) => String(id)),
    nodes,
    flows,
    bindings,
  };
}

function appendRuntimeNextFlows(
  nodeId: string,
  next: PropRuntimeNextMap | undefined,
  flows: PropDescriptionFlow[],
) {
  if (!next) return;
  Object.keys(next).forEach((portId) => {
    const targets = next[portId] ?? [];
    targets.forEach((targetId, index) => {
      flows.push({
        id: `${nodeId}:${portId}:${index}`,
        from: { nodeId, portId },
        to: { nodeId: String(targetId), portId: "flow" },
        order: index,
      });
    });
  });
}

function appendRuntimeInputBindings(
  nodeId: string,
  inputs: PropRuntimeInputMap | undefined,
  bindings: PropDescriptionBinding[],
) {
  if (!inputs) return;
  Object.keys(inputs).forEach((portId) => {
    const value = inputs[portId];
    const sourceIds = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];
    sourceIds.forEach((sourceId, index) => {
      bindings.push({
        id: `${nodeId}:${portId}:${index}`,
        source: { nodeId: String(sourceId), portId: "value" },
        target: { nodeId, portId },
      });
    });
  });
}
